import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CardActions,
  useTheme,
} from '@mui/material';
import {
  School,
  Work,
  People as Family,
  FlightTakeoff as Flight,
  Business,
  Gavel,
  CheckCircle,
  ArrowForward,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const serviceDetails = [
  {
    title: 'Student Visas',
    icon: School,
    description: 'Comprehensive support for international students pursuing education in the UK.',
    features: [
      'Student visa (Tier 4)',
      'Short-term study visas',
      'Child student visas',
      'Post-study work visas',
    ],
    link: '/services/student-visa',
  },
  {
    title: 'Work Visas',
    icon: Work,
    description: 'Expert guidance for professionals seeking employment opportunities in the UK.',
    features: [
      'Skilled Worker visa',
      'Health and Care Worker visa',
      'Intra-company visas',
      'Global Talent visa',
    ],
    link: '/services/work-visa',
  },
  {
    title: 'Family Visas',
    icon: Family,
    description: 'Supporting families in their journey to unite and settle in the UK.',
    features: [
      'Spouse/Partner visas',
      'Child dependant visas',
      'Parent visas',
      'Indefinite Leave to Remain',
    ],
    link: '/services/family-visa',
  },
  {
    title: 'Visitor Visas',
    icon: Flight,
    description: 'Assistance with various types of UK visitor visas for different purposes.',
    features: [
      'Standard visitor visa',
      'Business visitor visa',
      'Family visitor visa',
      'Medical treatment visa',
    ],
    link: '/services/visitor-visa',
  },
  {
    title: 'Business Visas',
    icon: Business,
    description: 'Specialized solutions for businesses and entrepreneurs.',
    features: [
      'Start-up visa',
      'Innovator visa',
      'Representative of overseas business',
      'UK expansion worker visa',
    ],
    link: '/services/business-visa',
  },
  {
    title: 'Appeals & Reviews',
    icon: Gavel,
    description: 'Professional support for challenging visa refusals and complex cases.',
    features: [
      'Administrative review',
      'Appeal preparation',
      'Judicial review',
      'Case reassessment',
    ],
    link: '/services/appeals',
  },
];

const Services = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h1" gutterBottom>
              Our Immigration Services
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              Comprehensive UK immigration solutions tailored to your needs
            </Typography>
            <Button
              component={RouterLink}
              to="/consultation"
              variant="contained"
              color="secondary"
              size="large"
              sx={{ px: 4, py: 1.5 }}
            >
              Free Consultation
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Services Grid */}
        <Grid container spacing={4}>
          {serviceDetails.map((service) => {
            const IconComponent = service.icon;
            return (
              <Grid item xs={12} md={6} key={service.title}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <IconComponent 
                        sx={{ 
                          fontSize: 40, 
                          color: 'primary.main',
                          mr: 2,
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }} 
                      />
                      <Typography variant="h5" component="h2">
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography color="textSecondary" paragraph>
                      {service.description}
                    </Typography>
                    <List>
                      {service.features.map((feature) => (
                        <ListItem key={feature}>
                          <ListItemIcon>
                            <CheckCircle color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      component={RouterLink}
                      to={service.link}
                      variant="outlined"
                      color="primary"
                      endIcon={<ArrowForward />}
                      fullWidth
                    >
                      Learn More
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* CTA Section */}
        <Box
          sx={{
            mt: 8,
            textAlign: 'center',
            bgcolor: 'background.paper',
            py: 8,
            px: 4,
            borderRadius: 4,
            boxShadow: 3,
          }}
        >
          <Typography variant="h3" gutterBottom>
            Need Help With Your Visa Application?
          </Typography>
          <Typography variant="h6" color="textSecondary" paragraph sx={{ mb: 4 }}>
            Contact us today for a free consultation and let our experts guide you through the process.
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ px: 6, py: 2 }}
          >
            Book Free Consultation
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Services; 